import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDialogState } from "reakit/Dialog";

import { UserContext } from "@/components/User";
import { IConsultantProfile, isProfileConnected } from "@/data/consultant-profiles";
import { BrandedLogo, LazyLoadedLogo, LogoPlaceholder } from "@/views/components/Logo";
import { ProfileType } from "@/views/components/ProfileTypePicker";
import { router } from "@/views/router";

export const useContextDropdown = () => {
  const User = useContext(UserContext);
  const history = useHistory();

  const [blockedProfile, setBlockedProfile] = useState<IConsultantProfile>();
  const blockedProfileModalState = useDialogState({ animated: true });
  const addLocationToProfileModalState = useDialogState({ animated: true });

  const [previewProfile, setPreviewProfile] = useState<IConsultantProfile | null>(null);
  const [isCreateCompanyModalOpen, setIsCreateSupplierModalOpen] = useState(false);
  const [isCreatePrivatePersonModalOpen, setIsCreatePrivatePersonModalOpen] = useState(false);
  const [isProfileTypePickerModalOpen, setIsProfileTypePickerModalOpen] = useState(false);

  const { canCreateProfile, sessionUser } = User;
  const { consultantProfiles } = sessionUser;

  const currentProfile = User.contextConsultantProfile;
  const currentCompany = User.contextCompany;

  const handleCreateCompanyModalClose = useCallback(() => {
    setIsCreateSupplierModalOpen(false);
  }, []);

  const handleCreatePrivatePersonModalClose = useCallback(() => {
    setIsCreatePrivatePersonModalOpen(false);
  }, []);

  const handleTypePickerModalClose = useCallback(() => {
    setIsProfileTypePickerModalOpen(false);
  }, []);

  const handleCreate = useCallback(() => {
    if (canCreateProfile()) {
      setIsProfileTypePickerModalOpen(true);
    } else {
      setIsCreateSupplierModalOpen(true);
    }
  }, [canCreateProfile, setIsProfileTypePickerModalOpen, setIsCreateSupplierModalOpen]);

  useEffect(() => {
    if (currentProfile && !isProfileConnected(currentProfile) && currentProfile?.location == null) {
      addLocationToProfileModalState.show();
    }
  }, [addLocationToProfileModalState, currentProfile]);

  const changeContext = (profileId: number) => {
    User.setConsultantProfileContext(profileId);
    history.push(router.get("dashboard").path, { profileContextChange: true });
  };

  const handleConsultantModalClose = () => setPreviewProfile(null);

  const isRequestToJoinAccepted = (consultantProfile: IConsultantProfile) => {
    return !consultantProfile.requestToJoin || consultantProfile.requestToJoin.status === "ACCEPTED";
  };

  const connectedProfiles = consultantProfiles.filter(cp => isProfileConnected(cp) && isRequestToJoinAccepted(cp));
  const personalProfiles = consultantProfiles.filter(cp => !isProfileConnected(cp));

  const handleConnectedProfileClick = (profile: IConsultantProfile) => {
    if (profile.blocked) {
      setBlockedProfile(profile);
      blockedProfileModalState.show();
      return;
    }

    if (profile.status === "PENDING") {
      setPreviewProfile(profile);
      return;
    }

    changeContext(profile.id);
  };

  const handlePersonalProfileClick = (profile: IConsultantProfile) => {
    changeContext(profile.id);
  };

  const handleProfileSelect = (profileType: ProfileType) => {
    setIsProfileTypePickerModalOpen(false);

    if (profileType === "private-person") {
      setIsCreatePrivatePersonModalOpen(true);
    } else {
      setIsCreateSupplierModalOpen(true);
    }
  };
  const baseClass = "navigation";
  const renderCompanyLogo = (size = "xs", iconSize = "md") => {
    return (
      <>
        <LazyLoadedLogo logo={User.contextCompany?.logo}>
          {publicUrl => (
            <BrandedLogo
              src={publicUrl}
              orientation="portrait"
              placeholder={<LogoPlaceholder size={size} iconSize={iconSize} />}
              size="xs"
              className={`${baseClass}__logo`}
            />
          )}
        </LazyLoadedLogo>
      </>
    );
  };

  return {
    blockedProfile,
    blockedProfileModalState,
    addLocationToProfileModalState,
    previewProfile,
    isCreateCompanyModalOpen,
    isCreatePrivatePersonModalOpen,
    isProfileTypePickerModalOpen,
    handleTypePickerModalClose,
    currentProfile,
    currentCompany,
    handleCreateCompanyModalClose,
    handleCreatePrivatePersonModalClose,
    handleCreate,
    handleConsultantModalClose,
    connectedProfiles,
    personalProfiles,
    handleConnectedProfileClick,
    handlePersonalProfileClick,
    handleProfileSelect,
    renderCompanyLogo,
  };
};
